import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui';
import ServiceIcon, { BookingTypeIcon, categoryTint } from '@/components/companion/ServiceIcon';
import {
  useServiceCategories,
  bookingTypeLabel,
  isRemote,
  type ServiceCategory,
} from '@/lib/data/services';
import { useAuth } from '@/lib/auth';
import { useWallet } from '@/lib/data/wallet';
import { AuthPromptModal, MembershipPromptModal } from './ServiceAccessModals';

interface ServiceCatalogueProps {
  id?: string;
}

export default function ServiceCatalogue({ id = 'services' }: ServiceCatalogueProps) {
  const navigate = useNavigate();
  const { session, profile } = useAuth();
  const { wallet } = useWallet();
  const { categories, loading, error } = useServiceCategories();

  const [activeFormat, setActiveFormat] = useState<string>('all');
  const [selectedForAuth, setSelectedForAuth] = useState<ServiceCategory | null>(null);
  const [selectedForMembership, setSelectedForMembership] = useState<ServiceCategory | null>(null);

  const isStaff = profile?.role === 'admin' || profile?.role === 'moderator';
  const hasMembership = wallet?.fees ? wallet.fees.userFeePaid || wallet.fees.userFeePaise === 0 || isStaff : isStaff;
  const userFeePaise = wallet?.fees?.userFeePaise ?? 19900;

  const handleCategoryClick = (category: ServiceCategory) => {
    // Rule 1: If not logged in -> ask for signup / login
    if (!session) {
      setSelectedForAuth(category);
      return;
    }


    // Rule 2: If logged in and has not paid membership -> ask for membership
    if (!hasMembership) {
      setSelectedForMembership(category);
      return;
    }

    // Otherwise: navigate directly to the companion list for this service
    navigate(`/services/${category.slug}`);
  };

  const filteredCategories = categories.filter((c) => {
    if (activeFormat === 'all') return true;
    if (activeFormat === 'remote') return c.allowedBookingTypes.some(isRemote);
    if (activeFormat === 'in_person') return c.allowedBookingTypes.includes('in_person');
    return true;
  });

  return (
    <section id={id} className="relative z-10 scroll-mt-20 border-t border-white/10 bg-[#080E1E]/80 py-24 text-white backdrop-blur-xs sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center justify-between gap-6 md:flex-row md:items-end">
          <div className="text-center md:text-left">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--cyan)]/40 bg-[var(--cyan)]/10 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-[var(--cyan)]">
              ✨ Live Service Directory
            </span>
            <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Find a partner for what you want to do
            </h2>
            <p className="mt-3 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              Every companion is thoroughly verified before listing. Choose a format and explore verified companions ready to connect.
            </p>
          </div>

          {/* Format Filter Segmented Bar with Vibrant Glowing Ring */}
          <div className="rounded-2xl bg-gradient-to-r from-[var(--purple)] via-[var(--brand)] to-[var(--brandpink)] p-[2px] shadow-[0_0_30px_-5px_rgba(123,47,224,0.45)]">
            <div className="flex flex-wrap items-center justify-center gap-1.5 rounded-[14px] bg-[#070D1F]/95 p-1.5 backdrop-blur-2xl">
              {[
                { id: 'all', label: 'All Formats', icon: '✨' },
                { id: 'in_person', label: 'In-Person', icon: '📍' },
                { id: 'remote', label: 'Online & Calls', icon: '💬' },
              ].map((fmt) => {
                const isActive = activeFormat === fmt.id;
                return (
                  <button
                    key={fmt.id}
                    type="button"
                    onClick={() => setActiveFormat(fmt.id)}
                    className={`flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-bold transition-all duration-200 sm:text-sm ${
                      isActive
                        ? 'btn-grad text-white shadow-lg shadow-[var(--brand)]/30 ring-1 ring-white/30 scale-[1.02]'
                        : 'text-slate-300 transition-colors hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <span>{fmt.icon}</span>
                    <span>{fmt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>


        {error && (
          <div className="mt-8 rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 text-center text-sm font-medium text-rose-300">
            Could not load live services. Please refresh to try again.
          </div>
        )}

        {loading ? (
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-56 animate-pulse rounded-3xl border border-white/10 bg-[#0B132B]/60"
              />
            ))}
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="mt-12 rounded-3xl border border-white/10 bg-[#0B132B]/60 p-12 text-center text-slate-300">
            No services match the selected filter.
          </div>
        ) : (
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredCategories.map((c, idx) => {
              const tint = categoryTint(c.hue);
              return (
                <div
                  key={c.id}
                  className="animate-rise group relative flex cursor-pointer flex-col justify-between overflow-hidden rounded-3xl border border-white/15 bg-[#0B132B]/90 p-6 shadow-xl backdrop-blur-md transition-all duration-300 hover:-translate-y-1.5 hover:border-[var(--brand)]/50 hover:bg-[#101B3B] hover:shadow-2xl hover:shadow-[var(--brand)]/15"
                  style={{ animationDelay: `${idx * 40}ms` }}
                  onClick={() => handleCategoryClick(c)}
                >
                  <div>
                    <div className="flex items-center justify-between">
                      <span
                        className="flex h-13 w-13 items-center justify-center rounded-2xl shadow-inner transition-transform duration-300 group-hover:scale-110"
                        style={tint}
                      >
                        <ServiceIcon iconKey={c.iconKey} size={26} />
                      </span>
                      <span className="flex items-center gap-1 text-xs font-bold text-[var(--cyan)] transition-colors group-hover:text-[var(--brand)]">
                        View Companions →
                      </span>
                    </div>

                    <h3 className="mt-4 text-xl font-extrabold text-white transition-colors duration-200 group-hover:text-[var(--brand)]">
                      {c.name}
                    </h3>
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-slate-300">
                      {c.description}
                    </p>
                  </div>

                  <div className="mt-6 flex flex-wrap items-center gap-1.5 border-t border-white/10 pt-4">
                    {c.allowedBookingTypes.map((t) => (
                      <span
                        key={t}
                        title={bookingTypeLabel(t)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-semibold text-slate-200"
                      >
                        <BookingTypeIcon type={t} size={13} />
                        {bookingTypeLabel(t)}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>


      {/* Auth Prompt Modal (when guest clicks a service) */}

      <AuthPromptModal
        category={selectedForAuth}
        onClose={() => setSelectedForAuth(null)}
      />

      {/* Membership Prompt Modal (when signed in user hasn't unlocked directory) */}
      <MembershipPromptModal
        category={selectedForMembership}
        userFeePaise={userFeePaise}
        onClose={() => setSelectedForMembership(null)}
      />
    </section>
  );
}
